import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

const VIDEO_SRC =
  "https://customer-assets.emergentagent.com/job_modern-ops-5/artifacts/royylgha_kling30_Video_20260429_105821.mp4";

const TICKER = (process.env.REACT_APP_BRAND_TICKER || "").trim();

/**
 * Full-viewport autoplay background video banner. Sits at the very top
 * of the homepage. The hero section ("Your leads...") begins one full
 * page below it.
 *
 * - HTML5 <video> with autoplay, muted, loop, playsinline, preload="auto"
 * - object-cover, full-bleed, responsive across desktop / tablet / mobile
 * - No controls, no play button — purely background motion
 * - Lazy attached on first viewport entry (this is the first section so
 *   it loads immediately after mount)
 * - Honours prefers-reduced-motion (video paused, poster shown)
 */
export default function VideoBanner() {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [canShow, setCanShow] = useState(false);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShouldLoad(true);
            io.disconnect();
          }
        });
      },
      { rootMargin: "0px", threshold: 0.05 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldLoad) return;
    const v = videoRef.current;
    if (!v) return;

    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const onCanPlay = () => setCanShow(true);
    v.addEventListener("loadeddata", onCanPlay);
    v.addEventListener("canplay", onCanPlay);

    if (!reduce) {
      const playPromise = v.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    }

    return () => {
      v.removeEventListener("loadeddata", onCanPlay);
      v.removeEventListener("canplay", onCanPlay);
    };
  }, [shouldLoad]);

  const scrollToHero = () => {
    if (typeof document === "undefined") return;
    document
      .querySelector('[data-testid="hero-section"]')
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      ref={containerRef}
      data-testid="video-banner"
      className="relative w-full min-h-screen overflow-hidden bg-[#F7F3EC] pt-16 sm:pt-20"
      aria-label="The Modern Exit — brand"
    >
      {/* Static wordmark — always rendered behind the video so the section
          looks intentional even if the video never loads (data saver,
          codec block, autoplay refused, slow network). */}
      <div
        aria-hidden="true"
        className="absolute inset-0 z-0 grid place-items-center select-none px-6"
      >
        <div className="text-center">
          <span className="font-display text-[34px] sm:text-6xl md:text-7xl lg:text-[7.5rem] tracking-[-0.025em] font-semibold text-[#171717]/90 leading-none">
            The Modern Exit
          </span>
          <p className="mt-3 sm:mt-4 font-mono text-[10px] sm:text-[12px] uppercase tracking-[0.28em] sm:tracking-[0.32em] text-[#5E5B55]">
            Funnels · to · Freedom
          </p>
        </div>
      </div>

      {/* Background video — fades in over the wordmark once playable */}
      {shouldLoad && (
        <video
          ref={videoRef}
          data-testid="video-banner-video"
          className={`absolute inset-0 z-[1] w-full h-full object-contain transition-opacity duration-700 ${
            canShow ? "opacity-100" : "opacity-0"
          }`}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/hero-poster.svg"
          aria-hidden="true"
        >
          <source src={VIDEO_SRC} type="video/mp4" />
        </video>
      )}

      {/* Cream poster fallback always rendered behind for instant paint */}
      <div
        aria-hidden="true"
        className="absolute inset-0 z-[-1] bg-[#F7F3EC]"
        style={{
          backgroundImage:
            "radial-gradient(1100px 600px at 92% -10%, rgba(165,106,67,0.10), transparent 60%), radial-gradient(900px 500px at -10% 30%, rgba(111,115,94,0.08), transparent 60%)",
        }}
      />

      {/* Edge vignette to frame the motion premium */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-10"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 50%, transparent 50%, rgba(247,243,236,0.35) 90%, rgba(236,228,216,0.55) 100%)",
        }}
      />

      {/* Live ticker pill — env-driven momentum signal */}
      {TICKER && (
        <div
          data-testid="video-banner-ticker"
          className="absolute z-20 left-1/2 -translate-x-1/2 bottom-20 sm:bottom-24 inline-flex items-center gap-2 rounded-full bg-[#11100E]/90 backdrop-blur px-3.5 sm:px-4 py-1.5 sm:py-2 border border-white/10 whitespace-nowrap shadow-[0_12px_28px_-12px_rgba(17,16,14,0.5)]"
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inset-0 rounded-full bg-[#A56A43]/70 animate-ping" />
            <span className="relative h-1.5 w-1.5 rounded-full bg-[#A56A43]" />
          </span>
          <span className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.22em] sm:tracking-[0.24em] text-[#F7F3EC]">
            {TICKER}
          </span>
        </div>
      )}

      {/* Scroll cue */}
      <button
        type="button"
        onClick={scrollToHero}
        data-testid="video-banner-scroll-cue"
        aria-label="Scroll to hero"
        className="absolute z-20 left-1/2 -translate-x-1/2 bottom-3 sm:bottom-5 inline-flex flex-col items-center justify-center gap-1 min-h-[44px] min-w-[44px] px-3 py-2 text-[#171717]/70 hover:text-[#171717] transition-colors"
      >
        <span className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.22em]">
          Scroll
        </span>
        <ChevronDown size={14} className="animate-drift" />
      </button>
    </section>
  );
}
